import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import AdminTable from "../../shared/table";
import IconDelete from "canvas-core-react/lib/IconDelete";
import { instanceAction } from "../../../Redux/api";
import StatusBadge from "canvas-core-react/lib/StatusBadge";
import ActionMenuList from "canvas-core-react/lib/ActionMenuList";
import IconShow from "canvas-core-react/lib/IconShow";
import IconContinueLater from "canvas-core-react/lib/IconContinueLater";
import ActionMenuListItem from "canvas-core-react/lib/ActionMenuListItem";
import CheckBox from "../../shared/custom-components/checkbox";
import PrimaryButton from "canvas-core-react/lib/PrimaryButton";
import Modal from "../../utils/customModal";
import {
  getNumberApprovals,
  infoContainer,
  loadingHtml,
  moduleAction,
  sucessInfoModal,
} from "../../utils/functions";
import useApi from "../../hooks/useApi";

import {
  MOD_DISCONT_PROV,
  MOD_RELATIONS,
  MOD_TERM_EXTENSION,
  WRITE,
} from "../../../permissions";
import TokenComponent from "../../shared/token";
import { getModules } from "../../../Redux/reducer/clients";
import { getAuthenticatedClientModules } from "../../../Redux/actions/clientActions";

import { getRoles, getSessionInfo } from "../../../Redux/reducer/session";
import { useHistory } from "react-router-dom";

const ProviderTable = ({
  getProvidersList,
  data,
  totalSize,
  handleTermExtensionRow,
  ...restProps
}) => {
  const [state, setState] = useState({
    page: 0,
    sizePerPage: restProps.sizePerPage,
    menus: [],
    selectedProviders: [],
    selectedRow: null,
    modal: { show: false, content: null, onClose: undefined },
    token: { show: false, code: null },
  });

  const { modules, roles, session } = useSelector((state) => ({
    roles: getRoles(state),
    modules: getModules(state),
    session: getSessionInfo(state),
  }));

  const { putApiCAll } = useApi();

  const { hasPermissions, canDelete, canEditTermExtension } = {
    hasPermissions: moduleAction(roles, MOD_DISCONT_PROV, WRITE),
    canDelete: moduleAction(roles, MOD_RELATIONS, WRITE),
    canEditTermExtension: moduleAction(roles, MOD_TERM_EXTENSION, WRITE),
  };

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    dispatch(getAuthenticatedClientModules());
  }, []);

  const handleTableChange = async ({ page, sizePerPage }) => {
    await getProvidersList(page, sizePerPage);
    setState((prev) => ({ ...prev, page, sizePerPage }));
  };

  const handleMenu = (id, open) => {
    const { menus } = state;
    menus[`menu-${id}`] = open;
    setState((prev) => ({ ...prev, menus }));
  };

  const getHeaders = () => {
    const { menus } = state;

    let headers = [];

    if (hasPermissions) {
      headers.push({
        selector: "checkbox",
        name: ``,
        grow: 0,
        center: true,
        bodyStyle: { flexGrow: 0, minWidth: "3rem", padding: "1.2rem 0.8rem" },
        cellFormatter: (row) =>
          row.toBillDiscount === false &&
          row.item.estado === "ACTIVE" &&
          ["PENDING", "REJECTED"].includes(
            row.item.provider.invitationStatus
          ) &&
          checkInput(row.item),
      });
    }
    headers.push(
      {
        grow: 2,
        selector: "empresa",
        name: `${t("providers.table.headers.company")}`,
        cellFormatter: (row) => (
          <p
            className="TextCaption__text TableBody__cellItem"
            title={row.empresa}
          >
            {row.empresa}
          </p>
        ),
      },
      {
        grow: 2,
        selector: "cedJurdica",
        name: `${t("providers.table.headers.identification")}`,
        cellFormatter: (row) => (
          <p
            className="TextCaption__text TableBody__cellItem"
            title={row.cedJurdica}
          >
            {row.cedJurdica}
          </p>
        ),
      }
    );
    if (session.allCompanies === true) {
      headers.push({
        selector: "companyName",
        name: t("invoices.table.headers.companyName"),
        cellFormatter: (row) => (
          <p
            className="TextCaption__text TableBody__cellItem"
            title={row.companyName}
          >
            {row.companyName}
          </p>
        ),
      });
    }
    headers.push(
      {
        grow: 1,
        selector: "facturas",
        name: `${t("providers.table.headers.invoices")}`,
      },
      {
        grow: 1,
        selector: "contacto",
        name: `${t("providers.table.headers.contact")}`,
      },
      {
        grow: 2,
        selector: "email",
        name: `${t("providers.table.headers.email")}`,
        cellFormatter: (row) => (
          <p
            className="TextCaption__text TableBody__cellItem"
            title={row.email}
          >
            {row.email}
          </p>
        ),
      },
      {
        grow: 1,
        selector: "type",
        name: `${t("providers.table.headers.type")}`,
      },
      {
        grow: 1,
        selector: "estado",
        name: `${t("providers.table.headers.state")}`,
      },
      {
        grow: 2,
        selector: "invitationState",
        name: `${t("providers.table.headers.invitationState")}`,
      },
      {
        selector: "accion",
        name: `${t("providers.table.headers.action")}`,
        center: true,
        grow: 1,
        cellFormatter: (row) => (
          <ActionMenuList
            dialogPosition="bottom-right"
            iconType="horizontal-small"
            bottomSheet={{
              heading: "Actions",
              secondaryAction: {
                onClick: () => {},
                label: "Cancel",
              },
            }}
            isMenuOpen={menus[`menu-${row.id}`] || false}
            setIsMenuOpen={(open) => handleMenu(row.id, open)}
          >
            <ActionMenuListItem
              icon={() => <IconShow className="TextButton__icon--left" />}
              onClick={() =>
                history.push(`/providers/${row.item.provider.id}/profile`)
              }
            >
              {t("detail")}
            </ActionMenuListItem>
            {canDelete && (
              <ActionMenuListItem
                icon={() => (
                  <IconDelete
                    id={`remove-${row.item.id}`}
                    className="TextButton__icon--left"
                  />
                )}
                onClick={() => deleteRelation(row)}
              >
                {t("general.buttons.deleteRelation")}
              </ActionMenuListItem>
            )}
            {row.canInvite && (
              <ActionMenuListItem onClick={() => resendInvitation(row)}>
                {t("general.buttons.resendInvitation")}
              </ActionMenuListItem>
            )}
            {handleTermExtensionRow && canEditTermExtension && (
              <ActionMenuListItem
                onClick={() => handleTerm(row)}
                icon={() => (
                  <IconContinueLater className="TextButton__icon--left" />
                )}
              >
                {t("general.buttons.termExtension")}
              </ActionMenuListItem>
            )}
          </ActionMenuList>
        ),
      }
    );
    return headers;
  };

  const handleTerm = ({ id, empresa, item, companyId }) => {
    const { toBillDiscount, globalApproval } = item.provider;

    handleMenu(id, false);
    setTimeout(
      () =>
        handleTermExtensionRow({
          title: empresa,
          country: item.provider.country.code,
          companyId,
          mode: !toBillDiscount ? 2 : toBillDiscount && globalApproval ? 0 : 1,
        }),
      100
    );
  };

  const resendInvitation = async ({ id }) => {
    handleMenu(id, false);
    const buttons = {
      formBtn: { action: closeAndReset, text: t("general.buttons.close") },
    };

    const notification = {
      title: t("providers.invitations.modal.title"),
      description: t("providers.invitations.modal.resend"),
    };

    setState((prev) => ({
      ...prev,
      modal: { show: true, onClose: null, content: loadingHtml() },
    }));

    try {
      await putApiCAll("/providers/forward-invitation", {
        providerId: id,
      });
    } catch (err) {
      notification.title = err.response.data.notifications[0]?.message;
      notification.description =
        err.response.data.notifications[0]?.description;
    }
    setState((prevState) => ({
      ...prevState,
      modal: {
        show: true,
        onClose: closeAndReset,
        content: infoContainer(
          notification.title,
          notification.description,
          buttons
        ),
      },
      token: { show: false, code: null },
    }));
  };

  const needToken = getNumberApprovals(modules, MOD_RELATIONS) === 0;

  const deleteRelation = ({ id, item: { provider } }) => {
    handleMenu(id, false);

    const selectedRow = {
      providerId: provider.id,
      accountCurrency: provider.currencyAux,
      companyId: provider.relation.company.id,
    };

    const buttonText = t("general.buttons", { returnObjects: true });

    setState((prev) => ({
      ...prev,
      selectedRow,
      modal: {
        show: true,
        content: sucessInfoModal(
          t("clients.deleteRelationTitle"),
          t("clients.deleteRelationDescription"),
          {
            SelBtn: {
              text: buttonText.cancel,
              action: () =>
                setState((prev) => ({
                  ...prev,
                  modal: { show: false, content: null, onClose: undefined },
                  selectedRow: null,
                })),
            },
            formBtn: {
              text: buttonText.remove,
              action: () => handleAction(selectedRow),
            },
          }
        ),
        onClose: null,
      },
    }));
  };

  const handleAction = (selectedRow) => {
    if (!needToken) return handleDelete(selectedRow);
    setState((prev) => ({
      ...prev,
      token: { show: true, code: null },
      modal: { show: false, content: null, onClose: undefined },
    }));
  };

  const loadingModal = () => {
    setState((prev) => ({
      ...prev,
      modal: { show: true, content: loadingHtml() },
    }));
  };

  const handleDelete = async (data, code) => {
    const requestData = data || state.selectedRow;

    let title = t("providers.deleteRelations.title");
    let description;
    let btnText = "accept";

    try {
      const headers = code ? { "x-security-code": code } : {};

      console.log({ code });
      loadingModal();

      await putApiCAll(
        "/providers/csa-remove-provider-company",
        requestData,
        headers
      );
      title = t("general.modal.success");
      description = t("general.modal.global_approval.delete_success");
    } catch (err) {
      description =
        err.response?.data?.notifications[0]?.description ||
        t("providers.deleteRelations.description");
      btnText = "accept";
      console.log(err);
    }
    setState((prevState) => ({
      ...prevState,
      modal: {
        show: true,
        onClose: closeAndReset,
        content: sucessInfoModal(title, description, {
          formBtn: {
            action: closeAndReset,
            text: t(`general.buttons.${btnText}`),
          },
        }),
      },
    }));
  };

  const setTableData = () => {
    var estado = null;
    var dataResult = [];

    if (data.length > 0) {
      data.map((item) => {
        if (item.estado === "TRANSITORY") {
          estado = (
            <StatusBadge className="pending client-status" type="default">
              {`${t("general.states.PENDING")}`}
            </StatusBadge>
          );
        } else if (item.estado === "ACTIVE") {
          estado = (
            <StatusBadge className="success client-status" type="success">
              {`${t("general.states.ACTIVE")}`}
            </StatusBadge>
          );
        } else if (item.estado === "INACTIVE") {
          estado = (
            <StatusBadge className="error1 client-status" type="error">
              {`${t("general.states.INACTIVE")}`}
            </StatusBadge>
          );
        } else if (item.estado === "REJECTED") {
          estado = (
            <StatusBadge
              className="rejected-payment client-status"
              type="error"
            >
              {`${t("general.states.REJECTED")}`}
            </StatusBadge>
          );
        }

        dataResult.push({
          id: item.id,
          estado: estado,
          email: item.email,
          empresa: item.empresa,
          facturas: item.facturas,
          contacto: item.contacto,
          cedJurdica: item.cedJurdica || "",
          companyName: item.provider?.relation?.companyName,
          companyId: item.provider?.relation?.company?.id,
          type: item.provider.toBillDiscount
            ? t("providerType.discount")
            : t("providerType.directPayment"),
          invitationState:
            item.provider.invitationStatus == ""
              ? "-"
              : t(`general.inviteStates.${item.provider.invitationStatus}`),
          toBillDiscount: item.provider.toBillDiscount,
          canInvite:
            item.estado === "ACTIVE" &&
            !item.provider?.toBillDiscount &&
            ["INVITED", "PROCESSED"].includes(
              item.provider?.invitationStatus
            ) &&
            hasPermissions,
          item,
        });
      });
    }

    return dataResult;
  };

  const checkInput = (item) => {
    const { selectedProviders } = state;

    const providerId = `provider-${item.id}`;

    var checkProps = {
      key: providerId,
      id: providerId,
      label: ``,
      name: providerId,
      onChange: (e) => changeSelected(e),
      checked: selectedProviders[providerId] !== undefined ? true : false,
    };

    return <CheckBox {...checkProps} />;
  };

  const changeSelected = (e) => {
    const { selectedProviders } = state;

    if (e.target.checked) {
      selectedProviders[e.target.id] = true;
    } else {
      delete selectedProviders[e.target.id];
    }

    setState((prev) => ({ ...prev, selectedProviders, selectedRow: null }));
  };

  const closeAndReset = () => {
    setState((prev) => ({
      ...prev,
      selectedProviders: [],
      selectedRow: null,
      modal: { show: false, content: null, onClose: undefined },
    }));
    handleTableChange({ page: 0, sizePerPage: state.sizePerPage });
  };

  const sendInvitation = async () => {
    const {
      selectedProviders,
      token: { code },
    } = state;

    let providers = [];

    Object.keys(selectedProviders).map((prov) =>
      providers.push({ providerId: prov.split("-")[1] })
    );

    setState((prev) => ({
      ...prev,
      modal: { show: true, content: loadingHtml() },
      selectedProviders: [],
    }));

    const apiHeader = code !== null ? { "x-security-code": code } : {};

    try {
      await dispatch(
        instanceAction(
          `/providers/send-invitation`,
          `put`,
          providers,
          apiHeader
        )
      );

      const buttons = {
        formBtn: {
          action: closeAndReset,
          text: t("general.buttons.close"),
        },
      };

      setState((prevState) => ({
        ...prevState,
        modal: {
          show: true,
          content: infoContainer(
            t("providers.invitations.modal.title"),
            t("providers.invitations.modal.message"),
            buttons
          ),
          onClose: closeAndReset,
        },
        token: { show: false, code: null },
      }));
    } catch (err) {
      const buttons = {
        formBtn: {
          action: () => closeAndReset(),
          text: t("general.buttons.close"),
        },
      };

      let content;

      if (
        err.response.data.notifications !== null &&
        err.response.data.notifications !== undefined
      ) {
        const notification = err.response.data.notifications[0];
        content = infoContainer(
          notification.message,
          notification.description,
          buttons
        );
      } else {
        content = infoContainer(
          t("general.labels.errorTitle"),
          t("general.labels.defaultError"),
          buttons
        );
      }

      setState((prevState) => ({
        ...prevState,
        modal: { show: true, onClose: closeAndReset, content },
        token: { show: false, code: null },
        selectedRow: null,
      }));
    }
  };

  const submitAction = () => {
    let approvals = getNumberApprovals(modules, MOD_DISCONT_PROV);
    if (approvals <= 0) {
      setState((prev) => ({ ...prev, token: { show: true, code: null } }));
    } else {
      sendInvitation();
    }
  };

  const getActions = () => {
    const { selectedProviders } = state;

    return (
      hasPermissions && (
        <div className="form-actions clearfix">
          <PrimaryButton
            id={`send-invitation`}
            disabled={Object.keys(selectedProviders).length <= 0}
            onClick={submitAction}
          >
            {t("general.buttons.sendInvitations")}
          </PrimaryButton>
        </div>
      )
    );
  };

  //895623
  const validate = ({ validToken = null }) => {
    if (validToken === null) {
      setState((prev) => ({ ...prev, token: { show: true, code: null } }));
    } else {
      setState((prev) => ({
        ...prev,
        token: { show: false, code: validToken },
      }));

      if (state.selectedRow) handleDelete(state.selectedRow, validToken);
      else sendInvitation();
    }
  };

  const { page, sizePerPage, modal, token } = state;

  const className = hasPermissions ? `checkbox-table` : ``;

  return (
    <>
      <AdminTable
        data={setTableData()}
        headers={getHeaders()}
        page={page}
        totalSize={totalSize}
        className={className}
        sizePerPage={sizePerPage}
        onTableChange={handleTableChange}
        actions={getActions()}
      />
      {token.show === true && (
        <TokenComponent
          send={validate}
          goBack={() =>
            setState((prev) => ({
              ...prev,
              token: { show: false, code: null },
            }))
          }
        />
      )}
      <Modal show={modal.show} handleClose={modal.onClose}>
        {modal.content}
      </Modal>
    </>
  );
};

export default ProviderTable;
